'use client';

// Client Component on purpose. The footer is chrome and must follow the active
// locale on every route, but locale propagation differs by rendering mode:
// Client Components read NextIntlClientProvider (set by app/[slug]/layout.tsx),
// while Server Components resolve against the next-intl REQUEST config — which
// cannot infer our locale because the segment is named [slug], not [locale].
//
// The result was a page that was half-translated: the nav (already client) went
// Spanish while this footer silently stayed English on the same screen. The
// alternative, calling setRequestLocale() in all 19 localised route files, is one
// forgotten call away from reintroducing exactly that. One directive here makes
// propagation uniform. The footer is also ALREADY client on /pricing and /sales,
// which import it from client pages, so this only makes the existing behaviour
// consistent.

import Link from 'next/link';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

import { localizeHref } from '@/i18n/localize-href';
import type { Locale } from '@/i18n/locales';

import { LanguageSwitcher } from './language-switcher';

import { FOOTER_COLUMNS, type FooterColumn } from '@/lib/marketing-footer';
import type { NavLinkItem } from '@/lib/marketing-nav';

// SAFETY: The surrounding typed contract establishes the asserted representation.
const FONT = { fontFamily: 'var(--font-fredoka)' } as const;

const LINK_CLASS =
  'block py-1.5 text-sm text-[rgb(var(--foreground-secondary))] hover:text-[rgb(var(--foreground))] transition-colors';

const HEADING_CLASS =
  'text-xs font-semibold text-[rgb(var(--foreground))] uppercase tracking-wider';

function isExternal(href: string): boolean {
  return href.startsWith('mailto:') || href.startsWith('http');
}

function FooterLink({ link }: { link: NavLinkItem }) {
  if (isExternal(link.href)) {
    return (
      <a href={link.href} className={LINK_CLASS} style={FONT}>
        {link.label}
      </a>
    );
  }

  return (
    <Link href={link.href} prefetch={link.prefetch ?? false} className={LINK_CLASS} style={FONT}>
      {link.label}
    </Link>
  );
}

function ColumnLinks({ column }: { column: FooterColumn }) {
  return (
    <ul className="space-y-1">
      {column.links.map((link) => (
        <li key={link.href}>
          <FooterLink link={link} />
        </li>
      ))}
      {column.hub ? (
        <li className="pt-1.5">
          <Link
            href={column.hub.href}
            prefetch={false}
            className="block py-1.5 text-sm font-medium text-[rgb(var(--foreground))] hover:opacity-80 transition-opacity"
            style={FONT}
          >
            {column.hub.label}
          </Link>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * Renders a single column. Desktop (md+): always-open block. Mobile: native
 * <details> accordion — zero JS, accessible, avoids a wall of links on phones.
 */
function FooterColumnBlock({ column }: { column: FooterColumn }) {
  return (
    <>
      {/* Mobile: collapsible */}
      <details className="md:hidden border-b border-[rgb(var(--surface-border))] py-3">
        <summary
          className={`${HEADING_CLASS} cursor-pointer list-none flex items-center justify-between`}
          style={FONT}
        >
          {column.heading}
          <span className="text-[rgb(var(--foreground-tertiary))] text-base leading-none transition-transform group-open:rotate-45">
            +
          </span>
        </summary>
        <div className="pt-3">
          <ColumnLinks column={column} />
        </div>
      </details>
      {/* Desktop: always-open */}
      <div className="hidden md:block">
        <h3 className={`${HEADING_CLASS} mb-4`} style={FONT}>
          {column.heading}
        </h3>
        <ColumnLinks column={column} />
      </div>
    </>
  );
}

const DIRECTORY_BADGES: {
  href: string;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  rel?: string;
}[] = [
  {
    href: 'https://saasfame.com/item/wisprs',
    src: 'https://saasfame.com/badge-dark.svg',
    alt: 'Featured on SaaS Fame',
  },
  {
    href: 'https://launchigniter.com/product/wisprs?ref=badge-wisprs',
    src: 'https://launchigniter.com/api/badge/wisprs?theme=dark',
    alt: 'Featured on LaunchIgniter',
    width: 212,
    height: 55,
  },
  // 7 directory badges removed 2026-07-17: these domains are Ahrefs-flagged spam and are in the
  // disavow list (disavow-wisprs-2026-07-16.txt). Stopped displaying their badges so we no longer
  // vouch for them sitewide (badge-for-backlink scheme adjacency, bible Ch. 6.2/6.5):
  // huntfortools.com, beamtools.com, softwarebolt.com, trustiner.com, aigc160.com, toolfinddir.com, startupvessel.com.
  {
    href: 'https://ashlist.com',
    src: 'https://ashlist.com/assets/images/badge-dark.png',
    alt: 'Featured on Ash List',
  },
  {
    href: 'https://besttoolvault.com',
    src: 'https://besttoolvault.com/assets/images/badge-dark.png',
    alt: 'Featured on Best Tool Vault',
  },
  {
    href: 'https://dang.ai',
    src: 'https://assets.dang.ai/badges/dang-verified-dark.png',
    alt: 'Verified on DANG!',
    width: 260,
    height: 94,
    rel: 'dofollow noopener',
  },
  {
    href: 'https://nicklaunches.com/products/wisprs/?utm_source=wisprs.co&utm_medium=badge&utm_campaign=featured',
    src: 'https://nicklaunches.com/badges/featured-dark.png',
    alt: 'Wisprs on Nick Launches',
    width: 244,
    height: 56,
    rel: 'dofollow noopener',
  },
  {
    href: 'https://www.scrolllaunch.com/products/wisprs?utm_source=badge&utm_medium=embed&utm_campaign=wisprs&ref=scrolllaunch',
    src: 'https://www.scrolllaunch.com/api/badge/wisprs',
    alt: 'Featured on ScrollLaunch',
    width: 220,
    height: 48,
    rel: 'dofollow noopener',
  },
  {
    href: 'https://startupbase.io/products/wisprs?utm_source=startupbase&utm_medium=badge&utm_campaign=featured-badge-dark',
    src: 'https://statics.startupbase.io/site/badges/featured-on-sb-dark.svg',
    alt: 'Featured on StartupBase',
    height: 55,
    rel: 'dofollow noopener',
  },
];

export function Footer() {
  // `useTranslations` (not `getTranslations`) on purpose: next-intl supports it in
  // both server and client components, and /pricing and /sales are client
  // components that render <Footer />. Making this async would break them.
  const t = useTranslations();
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const locale = useLocale() as Locale;

  // Label and href are localised independently: a translated label does NOT imply
  // a localised destination (/blog and /podcast have both a messageKey and an
  // English-only page), so the href goes through the filesystem-checked route set.
  const localizeLink = (link: NavLinkItem): NavLinkItem => ({
    ...link,
    label: link.messageKey ? t(link.messageKey) : link.label,
    href: localizeHref(link.href, locale),
  });

  // Labels without a messageKey stay English by design — their destinations are
  // untranslated pSEO pages (see src/lib/ui-copy.ts).
  const columns: FooterColumn[] = FOOTER_COLUMNS.map((column) => ({
    ...column,
    heading: column.headingKey ? t(column.headingKey) : column.heading,
    links: column.links.map(localizeLink),
    hub: column.hub ? localizeLink(column.hub) : undefined,
  }));

  return (
    <footer className="border-t border-[rgb(var(--surface-border))] bg-[rgb(var(--background))]">
      <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Brand block */}
        <div className="mb-10 md:mb-12 max-w-2xl">
          <Link href="/" className="flex items-center gap-1">
            <Image
              src="/logos/wisprs-logo.png"
              alt="Wisprs"
              width={56}
              height={42}
              className="flex-shrink-0 object-contain mt-1"
            />
            <span
              className="text-xl md:text-2xl font-black text-[rgb(var(--foreground))] leading-none tracking-tight"
              style={{ fontFamily: 'var(--font-fredoka)', fontWeight: 900 }}
            >
              wisprs
            </span>
          </Link>
          <p
            className="mt-4 text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed"
            style={FONT}
          >
            {t('ui.footer.tagline')}
          </p>
        </div>

        {/* Mega grid — 4 wide columns (2 even rows) on md+. Wider columns than an
            8-up row, so multi-word links no longer wrap to two lines, and the
            natural ordering puts the 4 long lists in row 1 and the 4 short lists
            in row 2 so each row reads as a balanced band. */}
        <div className="md:grid md:grid-cols-4 md:gap-x-8 md:gap-y-12">
          {columns.map((column) => (
            <FooterColumnBlock key={column.heading} column={column} />
          ))}
        </div>

        {/* Bottom rail: copyright + directory badges + support mailto */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-[rgb(var(--surface-border))]">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 gap-6">
            <div className="flex flex-col items-center gap-3 md:items-start">
              <p className="text-sm text-[rgb(var(--foreground-tertiary))]" style={FONT}>
                {t('ui.footer.copyright', { year: new Date().getFullYear() })}
              </p>
              <LanguageSwitcher />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:justify-end">
              {DIRECTORY_BADGES.map((b) => (
                <a
                  key={b.href}
                  href={b.href}
                  target="_blank"
                  rel={b.rel ?? 'noopener noreferrer'}
                  className="inline-flex shrink-0 opacity-90 transition-opacity hover:opacity-100"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element -- third-party directory badge */}
                  <img
                    src={b.src}
                    alt={b.alt}
                    className="h-[54px] w-auto"
                    {...(b.width ? { width: b.width } : {})}
                    {...(b.height ? { height: b.height } : {})}
                  />
                </a>
              ))}
              {/* rel="me" is the identity-consolidation signal that pairs with the
                  Organization `sameAs` in layout.tsx. Keep the two in sync: a
                  profile listed in one and not the other is a weaker claim, and
                  external scrapers (G2's profile enrichment, for one) look for a
                  visible link, not just JSON-LD. */}
              <a
                href="https://www.linkedin.com/company/wisprs/"
                target="_blank"
                rel="me noopener noreferrer"
                className="text-sm text-[rgb(var(--foreground-secondary))] hover:text-[rgb(var(--foreground))] transition-colors"
                style={FONT}
              >
                LinkedIn
              </a>
              <a
                href="mailto:tosh@getwisprs.com"
                className="text-sm text-[rgb(var(--foreground-secondary))] hover:text-[rgb(var(--foreground))] transition-colors"
                style={FONT}
              >
                tosh@getwisprs.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
